import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { environment } from '../../environments/environment';

type Diagnostic = Record<string, string | number | boolean>;

@Injectable({ providedIn: 'root' })
export class MobileInputDiagnosticsService implements OnDestroy {
  private started = false;
  private queue: Diagnostic[] = [];
  private dispose: Array<() => void> = [];
  private routerSubscription?: Subscription;
  private session = '';
  private device = '';
  private menuState: { before: boolean; after: boolean } | null = null;

  constructor(private zone: NgZone) {}

  start(router: Router): void {
    if (this.started || environment.production || location.protocol !== 'http:' ||
        !/^(localhost|127\.0\.0\.1|10\.\d+\.\d+\.\d+|192\.168\.\d+\.\d+|172\.(1[6-9]|2\d|3[01])\.\d+\.\d+)$/.test(location.hostname)) return;
    this.started = true;
    this.session = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
    this.device = /iPhone|iPod/i.test(navigator.userAgent) ? 'iPhone'
      : navigator.maxTouchPoints > 0 ? 'tactil' : 'PC';
    this.zone.runOutsideAngular(() => {
      const describe = (target: EventTarget | null): string => target instanceof Element
        ? `${target.tagName.toLowerCase()}.${Array.from(target.classList).slice(0, 3).join('.')}` : '(ninguno)';
      const route = (): string => location.pathname.split('/').map(part =>
        /^\d+$/.test(part) || /^[0-9a-f-]{32,}$/i.test(part) ? ':id' : part).join('/');
      let sequence = 0;
      let pending: {
        control: Element; id: number; start: number; x: number; y: number;
        moved: boolean; cancelled: boolean; clicked: boolean; hover: boolean; removed: boolean;
        prevented: boolean; changes: number; hoverChanges: number;
        hotspots: Map<string, number>; observer: MutationObserver; timer: number;
      } | null = null;
      const finish = (): void => {
        const tap = pending;
        if (!tap) return;
        tap.observer.disconnect();
        window.clearTimeout(tap.timer);
        pending = null;
        if (tap.moved || (tap.clicked && !tap.control.matches('.menu-toggle'))) return;
        const row: Diagnostic = {
          event: tap.cancelled ? 'toque-cancelado' : tap.clicked ? 'resultado-menu' : 'toque-sin-click', id: tap.id,
          target: describe(tap.control), route: route(), lagMs: Math.round(performance.now() - tap.start),
          replaced: tap.removed || !tap.control.isConnected, clicked: tap.clicked,
          prevented: tap.prevented, hover: tap.hover,
          changes: tap.changes, changesAfterHover: tap.hoverChanges,
          hotspots: [...tap.hotspots].sort((a, b) => b[1] - a[1]).slice(0, 3)
            .map(([key, count]) => `${key} (${count})`).join(' | '),
          stack: document.elementsFromPoint(tap.x, tap.y).slice(0, 3).map(describe).join(' > '),
        };
        if (tap.control.matches('.menu-toggle')) {
          row['angularHandler'] = this.menuState !== null;
          row['expectedOpen'] = this.menuState?.after ?? false;
          row['expandedInDom'] = tap.control.getAttribute('aria-expanded') || '(sin atributo)';
          if (this.menuState) row['previousOpen'] = this.menuState.before;
        }
        this.record(row);
      };
      const input = (event: Event): void => {
        const pointer = event as PointerEvent;
        if (event.type === 'pointerdown') {
          if (pointer.pointerType !== 'touch' || !pointer.isPrimary) return;
          finish();
          const control = event.target instanceof Element
            ? event.target.closest('button, a, summary, [role="button"]') : null;
          if (!control) return;
          this.menuState = null;
          const observer = new MutationObserver(records => {
            const tap = pending;
            if (!tap) return;
            for (const mutation of records) {
              tap.changes++;
              if (tap.hover) tap.hoverChanges++;
              for (const node of Array.from(mutation.removedNodes)) {
                if (node === control || node.contains(control)) tap.removed = true;
              }
              let node = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
              let component = node;
              for (let depth = 0; node && depth < 15; depth++, node = node.parentElement) {
                if (node.tagName.toLowerCase().startsWith('app-')) { component = node; break; }
              }
              const key = `${describe(component)}: ${mutation.type === 'childList'
                ? `hijos +${mutation.addedNodes.length}/-${mutation.removedNodes.length}` : mutation.attributeName}`;
              if (tap.hotspots.has(key) || tap.hotspots.size < 12) tap.hotspots.set(key, (tap.hotspots.get(key) || 0) + 1);
            }
          });
          pending = {
            control, id: ++sequence, start: performance.now(), x: pointer.clientX, y: pointer.clientY,
            moved: false, cancelled: false, clicked: false, hover: false, removed: false, prevented: false,
            changes: 0, hoverChanges: 0, hotspots: new Map(), observer,
            timer: window.setTimeout(finish, 4000),
          };
          observer.observe(document.body, { childList: true, subtree: true, attributes: true,
            attributeFilter: ['class', 'style', 'hidden', 'aria-expanded'] });
        } else if (pending) {
          if (event.type === 'pointermove' && Math.hypot(pointer.clientX - pending.x, pointer.clientY - pending.y) > 12) pending.moved = true;
          if (event.type === 'pointercancel') pending.cancelled = true;
          if (event.type === 'pointerup' || event.type === 'pointercancel') {
            window.clearTimeout(pending.timer);
            pending.timer = window.setTimeout(finish, 550);
          }
          if ((event.type === 'mouseover' || event.type === 'mousemove') &&
              event.target instanceof Node && pending.control.contains(event.target)) pending.hover = true;
          if (event.type === 'click' && event.target instanceof Node && pending.control.contains(event.target)) pending.clicked = true;
        }
        const tap = pending;
        if (tap) queueMicrotask(() => { tap.prevented ||= event.defaultPrevented; });
      };
      for (const name of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel', 'touchend', 'mouseover', 'mousemove', 'click']) {
        document.addEventListener(name, input, { capture: true, passive: true });
        this.dispose.push(() => document.removeEventListener(name, input, true));
      }
      this.dispose.push(() => { pending?.observer.disconnect(); window.clearTimeout(pending?.timer); pending = null; });
      const error = (event: ErrorEvent): void => this.record({ event: 'error', name: event.error?.name || 'Error', route: route() });
      const rejection = (event: PromiseRejectionEvent): void => this.record({ event: 'unhandledrejection', name: event.reason?.name || 'Rechazo', route: route() });
      window.addEventListener('error', error);
      window.addEventListener('unhandledrejection', rejection);
      this.dispose.push(() => window.removeEventListener('error', error), () => window.removeEventListener('unhandledrejection', rejection));
      let previous = performance.now();
      const timer = window.setInterval(() => {
        const now = performance.now();
        const lag = now - previous - 1000;
        previous = now;
        if (!document.hidden && lag > 1000) this.record({ event: 'hilo-ocupado', lagMs: Math.round(lag) });
        this.flush();
      }, 1000);
      this.dispose.push(() => window.clearInterval(timer));
      this.record({ event: 'diagnostico-resumido-listo', route: route() });
      this.flush();
    });
    this.routerSubscription = router.events.subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof NavigationCancel || event instanceof NavigationError) {
        this.record({ event: event.constructor.name, id: event.id });
      }
    });
  }

  menuHandled(before: boolean, after: boolean): void {
    if (this.started) this.menuState = { before, after };
  }

  private record(row: Diagnostic): void {
    if (this.queue.length < 100) this.queue.push({ session: this.session, device: this.device, ms: Math.round(performance.now()), ...row });
  }

  private flush(): void {
    if (!this.queue.length) return;
    const body = JSON.stringify(this.queue.splice(0, 10));
    // Un fallo del puente solo afecta al diagnóstico; nunca interfiere con la aplicación.
    void fetch('/__zivo_mobile_debug', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body, credentials: 'omit' })
      .then(response => { if (!response.ok) console.warn('[MOVIL] Puente de diagnóstico no disponible:', response.status); })
      .catch(() => console.warn('[MOVIL] Sin conexión con el puente de diagnóstico.'));
  }

  ngOnDestroy(): void {
    this.dispose.forEach(dispose => dispose());
    this.routerSubscription?.unsubscribe();
  }
}
